import { useCallback, useEffect, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { apiClient } from '@/api/client'
import { Button } from '@/components/ui/button'

// AI-FIRM-LIMIT-1 — daily AI limit per firm. Warning only: AI is never stopped.
// Nothing reaches the server until Save is pressed.

interface FirmRow {
  firm: string
  spent_usd: number
  limit_usd: number | null
  effective_limit_usd: number
  pct: number | null
  alert_80_sent: boolean
  alert_100_sent: boolean
  email_firm_admin: boolean
  firm_admin_email: string
}

interface LimitsResponse {
  rev: number
  settings: { default_usd: number; alert_to: string; firms: Record<string, unknown> }
  resolved_alert_to: string
  today: string
  firms?: FirmRow[]
}

// The server's 409 texts (stale save / simultaneous save): offer a Reload, never auto-reload or re-send.
const CONFLICT = /changed by someone else|same moment/

const DEFAULT_ERROR = 'Default daily limit must be more than $0 - at $0 no firm on the default gets an alert.'

interface Draft { limit: string; emailAdmin: boolean; adminEmail: string }

export function AiFirmLimitsSection() {
  const { data, loading, error, execute } = useApi<LimitsResponse>()
  const [defaultUsd, setDefaultUsd] = useState('5')
  const [alertTo, setAlertTo] = useState('')
  const [defaultError, setDefaultError] = useState('')
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})

  const load = useCallback(
    () => execute(() => apiClient.get<LimitsResponse>('/v1/admin/ai-firm-limits').then(res => res.data)),
    [execute],
  )

  useEffect(() => { load() }, [load])

  useEffect(() => {
    if (!data) return
    setDefaultUsd(String(data.settings?.default_usd ?? 5))
    setAlertTo(data.settings?.alert_to ?? '')
    const next: Record<string, Draft> = {}
    for (const f of data.firms ?? []) {
      next[f.firm] = {
        limit: f.limit_usd == null ? '' : String(f.limit_usd),
        emailAdmin: f.email_firm_admin,
        adminEmail: f.firm_admin_email ?? '',
      }
    }
    setDrafts(next)
  }, [data])

  const edit = (firm: string, patch: Partial<Draft>) =>
    setDrafts(prev => ({ ...prev, [firm]: { ...prev[firm], ...patch } }))

  const handleSave = useCallback(async () => {
    const defaultNum = Number(defaultUsd)
    if (defaultUsd.trim() === '' || Number.isNaN(defaultNum) || defaultNum <= 0) {
      setDefaultError(DEFAULT_ERROR)
      return
    }
    setDefaultError('')
    const firms: Record<string, unknown> = {}
    for (const [name, d] of Object.entries(drafts)) {
      firms[name] = {
        limit_usd: d.limit.trim() === '' ? null : Number(d.limit),
        email_firm_admin: d.emailAdmin,
        firm_admin_email: d.adminEmail.trim(),
      }
    }
    const result = await execute(() =>
      apiClient
        .put<LimitsResponse>('/v1/admin/ai-firm-limits', { default_usd: defaultNum, alert_to: alertTo.trim(), firms, rev: data?.rev ?? 0 })
        .then(res => res.data),
    )
    if (result) await load()
  }, [drafts, defaultUsd, alertTo, data?.rev, execute, load])

  const rows = data?.firms ?? []

  return (
    <section data-testid="ai-firm-limits-section" className="space-y-4">
      <h2 className="text-lg font-semibold text-gray-900">AI Daily Limits per Firm</h2>
      {error && (
        <div className="text-sm text-red-600">
          {error}
          {CONFLICT.test(error) && (
            <Button type="button" variant="outline" size="sm" data-testid="firm-limits-reload" onClick={() => load()} className="ml-3">Reload</Button>
          )}
        </div>
      )}
      {loading && <p className="text-sm text-gray-500">Loading…</p>}

      <div className="rounded-lg border bg-gray-50 px-4 py-3 grid gap-3 sm:grid-cols-2">
        <label className="text-sm text-gray-900">
          Default daily limit per firm ($)
          <input type="number" min="0" step="0.01" data-testid="firm-limit-default"
            className="mt-1 block w-full rounded border px-2 py-1"
            value={defaultUsd} onChange={e => setDefaultUsd(e.target.value)} />
          {defaultError && (
            <span data-testid="firm-limits-default-error" className="mt-1 block text-sm text-red-600">{defaultError}</span>
          )}
        </label>
        <label className="text-sm text-gray-900">
          Send alerts to
          <input type="email" data-testid="firm-limit-alert-to"
            className="mt-1 block w-full rounded border px-2 py-1"
            placeholder={data?.resolved_alert_to ?? ''}
            value={alertTo} onChange={e => setAlertTo(e.target.value)} />
        </label>
      </div>

      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-gray-600">
            <th>Firm</th><th>Spent today</th><th>Limit ($, blank = default)</th>
            <th>% used</th><th>Also email firm admin</th><th>Firm admin email</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(f => {
            const d = drafts[f.firm] ?? { limit: '', emailAdmin: false, adminEmail: '' }
            return (
              <tr key={f.firm} data-testid={`firm-limit-row-${f.firm}`}>
                <td>{f.firm}</td>
                <td data-testid={`firm-spent-${f.firm}`}>${f.spent_usd.toFixed(4)}</td>
                <td>
                  <input type="number" min="0" step="0.01" data-testid={`firm-limit-${f.firm}`}
                    className="w-24 rounded border px-2 py-1" value={d.limit}
                    onChange={e => edit(f.firm, { limit: e.target.value })} />
                </td>
                <td data-testid={`firm-pct-${f.firm}`}>{f.effective_limit_usd === 0 ? 'no alerts' : f.pct == null ? '—' : `${f.pct}%`}</td>
                <td>
                  <input type="checkbox" data-testid={`firm-email-admin-${f.firm}`} checked={d.emailAdmin}
                    onChange={e => edit(f.firm, { emailAdmin: e.target.checked })} />
                </td>
                <td>
                  <input type="email" data-testid={`firm-admin-email-${f.firm}`}
                    className="w-full rounded border px-2 py-1" value={d.adminEmail}
                    onChange={e => edit(f.firm, { adminEmail: e.target.value })} />
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
      <p data-testid="firm-limits-note" className="text-xs text-gray-500">
        Warning only — AI is never stopped by these limits. Alerts at 80% and 100%, once per day.
      </p>
      <div className="flex justify-end">
        <Button type="button" data-testid="firm-limits-save" onClick={handleSave}>Save</Button>
      </div>
    </section>
  )
}
